import { isNonEmptyString } from "@clipboard-health/util-ts";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import { Box, type SxProps, type Theme } from "@mui/material";
import { useTu } from "@src/appV2/i18n/useTu";
import { mergeSxProps } from "@src/appV2/lib";
import { useWorkplaceMenuPrivilegesProviderContext } from "@src/containers/redesign/AppLayout/Shared/Navigation/privilegesProviders/WorkplaceMenuPrivilegesProvider";

import { Button } from "../../components/Button";
import { routes } from "../../utils/routes";
import { ChatMessageTimestamp } from "../ChatMessageTimestamp";
import { type ChatMessage } from "../types";
import { formatMessageBubbleTime, formatWorkplaceTimestamp } from "../utils/formatMessageTime";
import { ChatMarkdownMessageBody } from "./ChatMarkdownMessageBody";

interface AutomatedMessageProps {
  message: ChatMessage;
  facilityTimeZone?: string;
  /**
   * When true (Clipboard admin viewer), the timestamp is shown as an explicit
   * workplace-local "WPL Time: …" label. Workplace customers leave this false.
   */
  isAdmin?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * Renders an automated `customType: "SYSTEM"` message as a workplace-sent
 * (outgoing) bubble labeled "Automated Message" with a markdown body.
 */
export function AutomatedMessage(props: AutomatedMessageProps) {
  const { message, facilityTimeZone, isAdmin = false, sx } = props;
  const { tu } = useTu("chat");
  const workplaceTimestamp = isAdmin
    ? formatWorkplaceTimestamp(message.createdAt, facilityTimeZone)
    : undefined;
  const { rulesAndExpectations: canAccessRulesAndExpectations } =
    useWorkplaceMenuPrivilegesProviderContext();
  // The message arrives as a SendBird UserMessage or AdminMessage; both carry
  // the body on `message`. File messages have no SYSTEM variant.
  const body = "message" in message ? message.message : undefined;

  if (!isNonEmptyString(body)) {
    return null;
  }

  return (
    <Box
      sx={mergeSxProps(
        {
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: 2,
        },
        sx
      )}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
        <ChatMessageTimestamp
          senderName={tu("Automated Message")}
          icon={
            <AutoAwesomeOutlinedIcon
              sx={(theme) => ({ fontSize: 14, color: theme.palette.base?.inactive })}
            />
          }
          timestamp={formatMessageBubbleTime(message.createdAt, facilityTimeZone)}
          workplaceTimestamp={workplaceTimestamp}
        />
        {canAccessRulesAndExpectations && (
          <Button
            variant="primary"
            size="small"
            startIconType="pencil"
            href={routes.facilityWorkerInstructions}
          >
            {tu("Update")}
          </Button>
        )}
      </Box>
      <Box
        data-dd-privacy="mask"
        sx={(theme: Theme) => ({
          display: "inline-flex",
          flexDirection: "column",
          paddingX: 4,
          paddingY: 3,
          borderRadius: theme.borderRadius?.xLarge,
          backgroundColor: theme.palette.background.chat?.outgoing ?? theme.palette.primary.main,
          maxWidth: "80%",
          overflowWrap: "break-word",
          wordBreak: "break-word",
        })}
      >
        <ChatMarkdownMessageBody text={body} isIncoming={false} />
      </Box>
    </Box>
  );
}
